// Sektör sözlüğü — TEK kaynak (2026-10-01 kullanıcı kararı: dosya adı kalıbı kalktı,
// sektör rapor İÇERİĞİNDEN ve müşteri kaydından gelir).
//
// Üç tüketici aynı kodları kullanır:
//   1. Framework Yazım Kiti (`docs/framework-kit/GOREV.md` sözlük tablosu) — ajan
//      `SEK-<KOD>` bloklarını bu kodlarla açar; drift testi tabloyu bu listeyle eşler.
//   2. Yayınlarken sektör paketi türetme (`services/sector-packages.ts`) — paket adı
//      `SECTOR_LABELS[kod]`.
//   3. Toplu ekranda paket önerisi (`lib/report-import-ui.ts`) — müşteri kaydının
//      serbest metin `sector` alanı ve kapak önerisi `normalizeSectorCode` ile koda iner.
//
// SAKLAMA: `clients.sector` serbest metin olarak KALIR (DB şekli değişmez); sözlük
// yalnız öneri/eşleme katmanıdır.
import { z } from "zod";

export const SECTOR_CODES = [
  "GYO",
  "BANKA",
  "SIGORTA",
  "FINANS",
  "KIMYA",
  "ENERJI",
  "GIDA",
  "PERAKENDE",
  "HOLDING",
  "OTOMOTIV",
  "TEKNOLOJI",
  "INSAAT",
  "TEKSTIL",
  "SAGLIK",
  "ULASTIRMA",
  "TELEKOM",
  "MADEN",
  "TURIZM",
  "SANAYI",
  "HIZMET",
] as const;

export const SectorCode = z
  .enum(SECTOR_CODES)
  .describe("Sektör kodu — kit `SEK-<KOD>` bloklarıyla aynı sözlük.");
export type SectorCode = z.infer<typeof SectorCode>;

/** Paket adı ve ekran etiketi — `SEK-GYO` bloğundan türetilen paketin adı budur. */
export const SECTOR_LABELS: Record<SectorCode, string> = {
  GYO: "Gayrimenkul Yatırım Ortaklığı",
  BANKA: "Bankacılık",
  SIGORTA: "Sigorta",
  FINANS: "Finans",
  KIMYA: "Kimya",
  ENERJI: "Enerji",
  GIDA: "Gıda ve İçecek",
  PERAKENDE: "Perakende",
  HOLDING: "Holding",
  OTOMOTIV: "Otomotiv",
  TEKNOLOJI: "Teknoloji",
  INSAAT: "İnşaat",
  TEKSTIL: "Tekstil",
  SAGLIK: "Sağlık",
  ULASTIRMA: "Ulaştırma ve Lojistik",
  TELEKOM: "Telekomünikasyon",
  MADEN: "Madencilik",
  TURIZM: "Turizm",
  SANAYI: "Sanayi",
  HIZMET: "Hizmet",
};

/**
 * Eş anlamlılar — SIRA ÖNEMLİ: özgül sektör genel olandan (SANAYI/HIZMET/HOLDING)
 * ÖNCE gelir, aksi halde "Kimya Sanayi A.Ş." SANAYI'ya düşerdi. İlk eşleşen kazanır.
 * MADEN, SANAYI'dan önce gelir: "Metal Ambalaj" → MADEN (`metal` kazanır) — bilinçli,
 * metal işleme sektör bloğu ambalaj imalatından daha ayırt edicidir.
 *
 * Anahtarlar katlanmış biçimde yazılır (küçük harf, ASCII, tek boşluk) ve
 * `matchesSynonym` ile **kelime başından** eşlenir; ham alt dize eşleşmesi YASAK
 * ("ve ticaret" içindeki "e ticaret" PERAKENDE'ye düşüyordu — inceleme bulgusu).
 */
const SECTOR_SYNONYMS: readonly (readonly [SectorCode, readonly string[]])[] = [
  ["GYO", ["gyo", "gayrimenkul yatirim ortakligi", "gayrimenkul yatirim", "gayrimenkul"]],
  ["BANKA", ["bankacilik", "banka", "katilim banka", "mevduat"]],
  ["SIGORTA", ["sigorta", "reasurans", "bireysel emeklilik", "emeklilik"]],
  [
    "FINANS",
    ["finansal kiralama", "leasing", "faktoring", "portfoy yonetimi", "araci kurum", "finans"],
  ],
  // run2 D4 — "poliuretan" eklendi: "Kimteks Poliüretan Sanayi ve Ticaret A.Ş." SANAYI'ya
  // düşüyordu ("kimteks" içindeki "kimya" kelime sınırında eşleşmez).
  [
    "KIMYA",
    ["petrokimya", "kimya", "polyester", "poliuretan", "elyaf", "plastik", "gubre", "boya"],
  ],
  [
    "ENERJI",
    ["enerji", "elektrik", "dogal gaz", "rafineri", "akaryakit", "ruzgar", "gunes", "petrol"],
  ],
  ["GIDA", ["gida", "icecek", "sut urun", "et urun", "unlu mamul", "tarim"]],
  ["PERAKENDE", ["perakende", "magazacilik", "zincir market", "market", "e ticaret"]],
  ["OTOMOTIV", ["otomotiv", "otomobil", "yedek parca", "lastik", "ticari arac"]],
  ["TEKNOLOJI", ["teknoloji", "bilisim", "yazilim", "elektronik", "savunma"]],
  ["INSAAT", ["insaat", "taahhut", "muteahhit", "cimento", "yapi malzeme"]],
  ["TEKSTIL", ["tekstil", "konfeksiyon", "hazir giyim", "iplik", "dokuma"]],
  ["SAGLIK", ["saglik", "hastane", "ilac", "medikal"]],
  [
    "ULASTIRMA",
    [
      "ulastirma",
      "tasimacilik",
      "lojistik",
      "havayolu",
      "havacilik",
      "denizcilik",
      "liman",
      "kargo",
    ],
  ],
  ["TELEKOM", ["telekomunikasyon", "telekom", "gsm", "mobil operator"]],
  ["MADEN", ["madencilik", "maden", "demir celik", "metal"]],
  ["TURIZM", ["turizm", "konaklama", "otel", "seyahat"]],
  ["HOLDING", ["holding"]],
  ["SANAYI", ["sanayi", "imalat", "makine", "ambalaj", "kagit", "cam"]],
  ["HIZMET", ["hizmet", "danismanlik", "egitim", "medya"]],
];

/**
 * run2 D4 — JENERİK kodlar: bir şirketi ayırt etmezler, yalnız daha özgül bir kod
 * uymadığında yazılır. Kapak önerisi bu kodlardan birini döndürdüğünde çağıran şirket
 * adından özgül bir kod türetmeyi dener (`preferSpecificSector`), çünkü ölçüldü (Sasa
 * Polyester): model ada bakıp 0,98 güvenle SANAYI yazdı, oysa şirket bir kimya/
 * petrokimya üreticisi ve KIMYA paketi önerilmesi gerekiyordu.
 */
export const GENERIC_SECTOR_CODES: readonly SectorCode[] = ["SANAYI", "HIZMET", "HOLDING"];

/** Kod jenerik mi (ayırt edici değil)? */
export function isGenericSectorCode(code: SectorCode | null | undefined): boolean {
  return code !== null && code !== undefined && GENERIC_SECTOR_CODES.includes(code);
}

/** Türkçe harfleri katlar, noktalama yerine boşluk koyar: "KİMYA SAN. A.Ş." → "kimya san a s". */
function fold(raw: string): string {
  return raw
    .replace(/[İIı]/g, "i")
    .replace(/[Şş]/g, "s")
    .replace(/[Ğğ]/g, "g")
    .replace(/[Üü]/g, "u")
    .replace(/[Öö]/g, "o")
    .replace(/[Çç]/g, "c")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * Anahtarın sözcük sınırında geçip geçmediği. Kurallar:
 *   - Eşleşme bir **kelimenin başında** başlar (sol sınır katı) — "ve ticaret"
 *     metni "e ticaret" anahtarını ARTIK eşlemez.
 *   - Çok kelimeli anahtar, kelime dizisi olarak aranır (anahtarlar tek boşlukludur).
 *   - Sağ uçta Türkçe çekim eki serbesttir, ama yalnız son sözcüğü ≥4 harf olan
 *     anahtarlarda: "banka" → "bankası" ✓, "sigorta" → "sigortacılık" ✓, ama
 *     "cam" → "çamaşır" ✗ (kısa anahtar yalnız tam kelimeyle eşleşir).
 */
function matchesSynonym(text: string, key: string): boolean {
  const lastWord = key.slice(key.lastIndexOf(" ") + 1);
  const suffixAllowed = lastWord.length >= 4;
  for (let from = 0; from <= text.length - key.length; from += 1) {
    const i = text.indexOf(key, from);
    if (i === -1) return false;
    const leftOk = i === 0 || text[i - 1] === " ";
    const after = text[i + key.length];
    const rightOk = after === undefined || after === " " || suffixAllowed;
    if (leftOk && rightOk) return true;
    from = i;
  }
  return false;
}

/**
 * Serbest metni (müşteri kaydının `sector` alanı, kapak önerisi, kit bölüm kodu
 * gövdesi) sözlük koduna indirir; hiçbir anahtar geçmiyorsa `null` — uydurma kod
 * yazılmaz, alan boş kalır ve kullanıcı seçer.
 */
export function normalizeSectorCode(raw: string | null | undefined): SectorCode | null {
  if (typeof raw !== "string") return null;
  const text = fold(raw);
  if (text.length === 0) return null;
  const compact = text.replace(/ /g, "");
  const exact = SECTOR_CODES.find((c) => fold(c).replace(/ /g, "") === compact);
  if (exact) return exact;
  for (const [code, keys] of SECTOR_SYNONYMS) {
    for (const k of keys) if (matchesSynonym(text, k)) return code;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Kit sektör bloğu (`SEK-<KOD>`) — önek kuralı TEK kaynak
// ---------------------------------------------------------------------------

/** Kit sektör bloğu bölüm kodu öneki. Tüketiciler kendi regex'ini yazmaz. */
export const SEK_BLOCK_RE = /^SEK[-_]/i;

/** `SEK-GYO` / `sek_kimya` → true; `FR-1`, `EFR-1`, `SEKTOR` → false. */
export function isSectorBlockCode(sectionCode: string): boolean {
  return SEK_BLOCK_RE.test(sectionCode);
}

/** `SEK-GYO` → `GYO`; öneki olmayan kod aynen döner. */
export function stripSectorPrefix(sectionCode: string): string {
  return sectionCode.replace(SEK_BLOCK_RE, "");
}
